import React, { useState } from 'react';
import { Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { markReviewHelpful } from '../services/reviewService';

// Generate a consistent avatar color based on nickname/id
const getAvatarColor = (seed) => {
  const colors = [
    'bg-rose-100 text-rose-600',
    'bg-amber-100 text-amber-600', 
    'bg-emerald-100 text-emerald-600',
    'bg-sky-100 text-sky-600',
    'bg-violet-100 text-violet-600',
    'bg-pink-100 text-pink-600',
    'bg-teal-100 text-teal-600',
    'bg-orange-100 text-orange-600'
  ];
  const hash = String(seed).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Get display name - now saved in database, fallback for legacy reviews
const getDisplayName = (nickname, id) => {
  if (nickname) return nickname;
  // Fallback for legacy reviews without saved nickname
  const adjectives = ['Happy', 'Radiant', 'Glowing', 'Fresh', 'Bright', 'Dewy', 'Lovely', 'Sweet'];
  const nouns = ['Bee', 'Butterfly', 'Star', 'Moon', 'Petal', 'Cloud', 'Bloom', 'Wave'];
  const hash = String(id).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `${adjectives[hash % adjectives.length]} ${nouns[(hash + 3) % nouns.length]}`;
};

const ReviewCard = ({ review }) => {
  const getStoredVote = () => {
    try {
      const storedVotes = localStorage.getItem('reviewVotes');
      if (storedVotes) {
        const votes = JSON.parse(storedVotes);
        return votes[review._id] || null;
      }
    } catch (error) {
      console.error('Error reading stored votes:', error);
    }
    return null;
  };

  const [helpfulVote, setHelpfulVote] = useState(getStoredVote());
  const [helpfulCount, setHelpfulCount] = useState(review.helpfulVotes || 0);
  const [notHelpfulCount, setNotHelpfulCount] = useState(review.notHelpfulVotes || 0);
  const [isVoting, setIsVoting] = useState(false);

  const storeVote = (voteType) => {
    try {
      const storedVotes = localStorage.getItem('reviewVotes');
      const votes = storedVotes ? JSON.parse(storedVotes) : {};
      if (voteType === null) {
        delete votes[review._id];
      } else {
        votes[review._id] = voteType;
      }
      localStorage.setItem('reviewVotes', JSON.stringify(votes));
    } catch (error) {
      console.error('Error storing vote:', error);
    }
  };
  
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.25;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<Star key={i} className="h-3 w-3 text-[#ab3d22] fill-current" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <div key={i} className="relative inline-block">
            <Star className="h-3 w-3 text-gray-200" />
            <Star className="h-3 w-3 text-[#ab3d22] fill-current absolute top-0 left-0" style={{ clipPath: 'inset(0 50% 0 0)' }} />
          </div>
        );
      } else {
        stars.push(<Star key={i} className="h-3 w-3 text-gray-200" />);
      }
    }
    return stars;
  };

  const handleHelpfulVote = async (voteType) => {
    if (isVoting) return;
    
    try {
      setIsVoting(true);
      
      if (helpfulVote === voteType) {
        setHelpfulVote(null);
        storeVote(null);
        if (voteType === 'up') {
          setHelpfulCount(prev => Math.max(0, prev - 1));
        } else {
          setNotHelpfulCount(prev => Math.max(0, prev - 1));
        }
      } else if (helpfulVote === null) {
        const isHelpful = voteType === 'up';
        await markReviewHelpful(review._id, isHelpful);
        setHelpfulVote(voteType);
        storeVote(voteType);
        if (isHelpful) {
          setHelpfulCount(prev => prev + 1);
        } else {
          setNotHelpfulCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Error voting on review:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const getRepurchaseDisplay = () => {
    switch (review.wouldRepurchase) {
      case 'yes':
        return { icon: '✓', text: 'Would repurchase', classes: 'bg-green-50 text-green-700 border-green-200' };
      case 'no':
        return { icon: '✗', text: 'Would not repurchase', classes: 'bg-red-50 text-red-700 border-red-200' };
      case 'maybe':
        return { icon: '~', text: 'Might repurchase', classes: 'bg-amber-50 text-amber-700 border-amber-200' };
      default:
        return null;
    }
  };

  const formatSkinType = (type) => {
    const labels = { dry: 'Dry', oily: 'Oily', combination: 'Combo', normal: 'Normal', sensitive: 'Sensitive' };
    return labels[type] || type;
  };

  const formatAgeRange = (range) => {
    const labels = { 'under-18': '<18', '18-24': '18-24', '25-34': '25-34', '35-44': '35-44', '45-54': '45-54', '55+': '55+' };
    return labels[range] || range;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  const repurchaseDisplay = getRepurchaseDisplay();
  const displayName = getDisplayName(review.nickname, review._id);
  const avatarColor = getAvatarColor(review._id);
  const avatarInitial = displayName.charAt(0).toUpperCase();
  // Get skinType and ageRange - handle empty strings as null
  const rawSkinType = review.skinType || review.context?.skinType;
  const rawAgeRange = review.ageRange;
  const skinType = rawSkinType && rawSkinType.trim() !== '' ? rawSkinType : null;
  const ageRange = rawAgeRange && rawAgeRange.trim() !== '' ? rawAgeRange : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3.5 hover:shadow-sm transition-shadow">
      {/* Header: Avatar, Name, Badges, Date */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Avatar */}
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor}`}>
            <span className="text-xs font-semibold">{avatarInitial}</span>
          </div>
          
          {/* Name and badges */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-800 text-sm">{displayName}</span>
            </div>
            
            {/* Metadata pills row - COSME style */}
            <div className="flex items-center gap-1.5 mt-0.5">
              {(skinType || ageRange) && (
                <span className="text-[11px] text-gray-500">
                  {skinType && formatSkinType(skinType)}
                  {skinType && ageRange && <span className="mx-1 text-gray-300">·</span>}
                  {ageRange && formatAgeRange(ageRange)}
                </span>
              )}
              {(skinType || ageRange) && <span className="text-gray-300">|</span>}
              <div className="flex items-center gap-0.5">
                {renderStars(review.rating)}
              </div>
              <span className="text-[10px] text-gray-400">·</span>
              <span className="text-[10px] text-gray-400">{formatDate(review.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Review Text */}
      {review.quickTake && (
        <p className="text-gray-700 text-sm leading-relaxed mb-2 pl-9">
          {review.quickTake}
        </p>
      )}

      {/* Footer: Repurchase + Helpful */}
      <div className="flex items-center justify-between pl-9 pt-2 border-t border-gray-100">
        {repurchaseDisplay && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${repurchaseDisplay.classes}`}>
            <span className="font-bold">{repurchaseDisplay.icon}</span>
            {repurchaseDisplay.text}
          </span>
        )}

        {/* Helpful section - lighter styling */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-300 italic">Helpful?</span>
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => handleHelpfulVote('up')}
              disabled={isVoting}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded transition-colors text-[10px] ${
                helpfulVote === 'up'
                  ? 'bg-green-50 text-green-600'
                  : isVoting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600 cursor-pointer'
              }`}
            >
              <ThumbsUp className="h-3 w-3" />
              {helpfulCount > 0 && <span>{helpfulCount}</span>}
            </button>
            <button
              onClick={() => handleHelpfulVote('down')}
              disabled={isVoting}
              className={`p-0.5 rounded transition-colors ${
                helpfulVote === 'down'
                  ? 'bg-red-50 text-red-500'
                  : isVoting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-500 cursor-pointer'
              }`}
            >
              <ThumbsDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewCard;
